/**
 * End-to-end sealed requests to a machine through relays it does not trust
 * (backend/e2e). Each request is sealed to the machine's key with HPKE
 * (RFC 9180 base mode, DHKEM(P-256, HKDF-SHA256), HKDF-SHA256, AES-128-GCM);
 * the response streams back in frames keyed by that request's exporter.
 */

import { isRouteGatewayFailure } from "./route-errors"

const KEM_ID = 0x0010, KDF_ID = 0x0001, AEAD_ID = 0x0001
const INFO = "mux e2e v1"
const REQUEST_AAD = "mux e2e request"
const NULL_BODY_STATUSES = new Set([101, 103, 204, 205, 304])

// How far each route's machine clock runs from this browser's, learned from
// its clock refusals. The gateway refuses envelopes sent before it started, so
// a browser running behind would otherwise fail every request after a restart.
const clockOffsets = new Map<string, number>()

/** Reads (and consumes) the gateway's clock refusal. A relay can forge it, but only to refuse. */
async function refusedClock(outer: Response) {
  if (outer.status !== 400 || !outer.headers.get("Content-Type")?.startsWith("application/json")) return false
  const refusal: unknown = await outer.clone().json().catch(() => undefined)
  return typeof refusal === "object" && refusal !== null && "error" in refusal && refusal.error === "e2e_clock_skew"
}

const encoder = new TextEncoder()
// WebCrypto takes buffers backed by an ArrayBuffer, not a SharedArrayBuffer.
type Bytes = Uint8Array<ArrayBuffer>
const bytes = (value: string): Bytes => encoder.encode(value)
const concat = (...parts: Uint8Array[]): Bytes => {
  const out = new Uint8Array(parts.reduce((total, part) => total + part.length, 0))
  let offset = 0
  for (const part of parts) { out.set(part, offset); offset += part.length }
  return out
}
const i2osp = (value: number, length: number) => {
  const out = new Uint8Array(length)
  for (let i = length - 1; i >= 0; i--) { out[i] = value & 0xff; value = Math.floor(value / 256) }
  return out
}
const KEM_SUITE = concat(bytes("KEM"), i2osp(KEM_ID, 2))
const HPKE_SUITE = concat(bytes("HPKE"), i2osp(KEM_ID, 2), i2osp(KDF_ID, 2), i2osp(AEAD_ID, 2))

async function hmac(key: Bytes, data: Bytes): Promise<Bytes> {
  // An empty HKDF salt is HashLen zeros (RFC 5869); WebCrypto refuses empty HMAC keys.
  const imported = await crypto.subtle.importKey("raw", key.length ? key : new Uint8Array(32), { name: "HMAC", hash: "SHA-256" }, false, ["sign"])
  return new Uint8Array(await crypto.subtle.sign("HMAC", imported, data))
}
const labeledExtract = (suite: Bytes, salt: Bytes, label: string, ikm: Bytes) =>
  hmac(salt, concat(bytes("HPKE-v1"), suite, bytes(label), ikm))
async function labeledExpand(suite: Bytes, prk: Bytes, label: string, info: Bytes, length: number) {
  const labeled = concat(i2osp(length, 2), bytes("HPKE-v1"), suite, bytes(label), info)
  let block: Bytes = new Uint8Array(0), out: Bytes = new Uint8Array(0)
  for (let counter = 1; out.length < length; counter++) {
    block = await hmac(prk, concat(block, labeled, Uint8Array.of(counter)))
    out = concat(out, block)
  }
  return out.slice(0, length)
}

const xorNonce = (base: Bytes, seq: number) => {
  const nonce = base.slice()
  const view = new DataView(nonce.buffer)
  view.setUint32(8, view.getUint32(8) ^ (seq >>> 0))
  view.setUint32(4, view.getUint32(4) ^ Math.floor(seq / 2 ** 32))
  return nonce
}

const aesKey = (raw: Bytes) => crypto.subtle.importKey("raw", raw, "AES-GCM", false, ["encrypt", "decrypt"])

type HpkeSender = {
  enc: Bytes
  seal: (aad: Bytes, plaintext: Bytes) => Promise<Bytes>
  export: (context: string, length: number) => Promise<Bytes>
}

const EMPTY = new Uint8Array(0)
// Base mode's key schedule context depends only on the suite and INFO, so every request shares it.
let keyScheduleContext: Promise<Bytes> | undefined
const baseKeyScheduleContext = () => keyScheduleContext ??= Promise.all([
  labeledExtract(HPKE_SUITE, EMPTY, "psk_id_hash", EMPTY),
  labeledExtract(HPKE_SUITE, EMPTY, "info_hash", bytes(INFO)),
]).then(([pskIDHash, infoHash]) => concat(Uint8Array.of(0), pskIDHash, infoHash))

/** SetupBaseS, with info INFO. */
async function setupSender(recipientPublicKey: Bytes): Promise<HpkeSender> {
  const [pair, recipient, context] = await Promise.all([
    crypto.subtle.generateKey({ name: "ECDH", namedCurve: "P-256" }, false, ["deriveBits"]),
    crypto.subtle.importKey("raw", recipientPublicKey, { name: "ECDH", namedCurve: "P-256" }, false, []),
    baseKeyScheduleContext(),
  ])
  const [dh, enc] = await Promise.all([
    crypto.subtle.deriveBits({ name: "ECDH", public: recipient }, pair.privateKey, 256).then(bits => new Uint8Array(bits)),
    crypto.subtle.exportKey("raw", pair.publicKey).then(raw => new Uint8Array(raw)),
  ])
  const shared = await labeledExpand(KEM_SUITE, await labeledExtract(KEM_SUITE, EMPTY, "eae_prk", dh), "shared_secret", concat(enc, recipientPublicKey), 32)
  const secret = await labeledExtract(HPKE_SUITE, shared, "secret", EMPTY)
  const [key, baseNonce, exporter] = await Promise.all([
    labeledExpand(HPKE_SUITE, secret, "key", context, 16).then(aesKey),
    labeledExpand(HPKE_SUITE, secret, "base_nonce", context, 12),
    labeledExpand(HPKE_SUITE, secret, "exp", context, 32),
  ])
  let seq = 0
  return {
    enc,
    seal: async (aad, plaintext) => new Uint8Array(await crypto.subtle.encrypt({ name: "AES-GCM", iv: xorNonce(baseNonce, seq++), additionalData: aad }, key, plaintext)),
    export: (exporterContext, length) => labeledExpand(HPKE_SUITE, exporter, "sec", bytes(exporterContext), length),
  }
}

export function decodeE2EPublicKey(value: string): Bytes {
  const raw = Uint8Array.from(atob(value), char => char.charCodeAt(0))
  if (raw.length !== 65 || raw[0] !== 4) throw new Error("Invalid machine encryption key")
  return raw
}

/** Opens a sealed response's frames in order; the final frame proves nothing was cut. */
function responseFrames(body: ReadableStream<Uint8Array>, key: CryptoKey, base: Bytes, signal?: AbortSignal | null) {
  const reader = body.getReader()
  let buffered: Bytes = new Uint8Array(0), seq = 0, finished = false
  const open = async (frame: Bytes) => {
    const iv = xorNonce(base, seq)
    for (const final of [0, 1]) {
      try {
        const plaintext = new Uint8Array(await crypto.subtle.decrypt({ name: "AES-GCM", iv, additionalData: Uint8Array.of(final) }, key, frame))
        seq++
        return { final: final === 1, plaintext }
      } catch { /* not this frame kind */ }
    }
    throw new TypeError("Machine response failed authentication")
  }
  /** The next frame's plaintext, or undefined after the final frame. */
  const next = async (): Promise<Bytes | undefined> => {
    while (!finished) {
      if (buffered.length >= 4) {
        const size = new DataView(buffered.buffer, buffered.byteOffset).getUint32(0)
        if (buffered.length >= 4 + size) {
          const frame = await open(buffered.slice(4, 4 + size))
          buffered = buffered.slice(4 + size)
          if (frame.final) { finished = true; reader.releaseLock(); return }
          return frame.plaintext
        }
      }
      const { value, done } = await reader.read()
      if (done) {
        signal?.throwIfAborted()
        // TypeError, as fetch reports a dropped connection.
        throw new TypeError("Machine response ended early")
      }
      buffered = concat(buffered, value)
    }
  }
  return { next, cancel: (reason?: unknown) => reader.cancel(reason) }
}

/**
 * fetch(base + path, init), sealed. Headers, including credentials, travel
 * inside the envelope; the relay sees only POST {base}/e2e. Only a sealed
 * answer is the machine's. A gateway failure comes back as a bodiless status,
 * so route recovery still sees 502s; every other unsealed answer, which the
 * relay could have written, throws.
 */
export async function sealedFetch(fetchImpl: typeof fetch, publicKey: string, base: string, path: string, init: RequestInit = {}) {
  // Request serializes every body type and supplies its Content-Type.
  const request = new Request(`${base}${path}`, { ...init, signal: undefined })
  const headers: Record<string, string[]> = {}
  request.headers.forEach((value, name) => { (headers[name.toLowerCase()] ??= []).push(value) })
  const body = new Uint8Array(await request.arrayBuffer())
  const route = `${publicKey} ${base}`

  const send = async () => {
    const head = bytes(JSON.stringify({ method: request.method, path, headers, time: Date.now() + (clockOffsets.get(route) ?? 0) }))
    const sender = await setupSender(decodeE2EPublicKey(publicKey))
    const sealed = await sender.seal(bytes(REQUEST_AAD), concat(i2osp(head.length, 4), head, body))
    const outer = await fetchImpl(`${base}/e2e`, {
      method: "POST",
      body: concat(sender.enc, sealed),
      headers: { "Content-Type": "application/octet-stream" },
      signal: init.signal,
      cache: "no-store",
    })
    return { outer, sender }
  }

  let { outer, sender } = await send()
  if (await refusedClock(outer)) {
    // The machine's clock, at the second the Date header gives, plus half of it.
    const machineTime = Date.parse(outer.headers.get("Date") ?? "")
    if (Number.isNaN(machineTime)) return new Response(null, { status: 503, statusText: "e2e_clock_skew" })
    clockOffsets.set(route, machineTime + 500 - Date.now());
    ({ outer, sender } = await send())
    if (await refusedClock(outer)) return new Response(null, { status: 503, statusText: "e2e_clock_skew" })
  }
  if (!outer.ok || !outer.body || outer.headers.get("Content-Type") !== "application/octet-stream") {
    void outer.body?.cancel().catch(() => {})
    if (isRouteGatewayFailure(outer.status)) return new Response(null, { status: outer.status, statusText: outer.statusText })
    // TypeError, as fetch reports a connection that did not reach the machine.
    throw new TypeError(`Machine route returned an unsealed response (${outer.status})`)
  }
  const [responseKey, responseBase] = await Promise.all([
    sender.export("mux e2e response key", 16).then(aesKey),
    sender.export("mux e2e response nonce", 12),
  ])
  const { next, cancel } = responseFrames(outer.body, responseKey, responseBase, init.signal)
  const first = await next()
  if (!first) throw new TypeError("Machine response had no head")
  const responseHead = JSON.parse(new TextDecoder().decode(first)) as { status: number; headers: Record<string, string[]> | null }
  const responseHeaders = new Headers()
  for (const [name, values] of Object.entries(responseHead.headers ?? {})) for (const value of values) responseHeaders.append(name, value)
  const stream = new ReadableStream<Uint8Array>({
    async pull(controller) {
      const chunk = await next()
      if (chunk) controller.enqueue(chunk)
      else controller.close()
    },
    cancel,
  })
  if (NULL_BODY_STATUSES.has(responseHead.status)) {
    void stream.cancel().catch(() => {})
    return new Response(null, { status: responseHead.status, headers: responseHeaders })
  }
  return new Response(stream, { status: responseHead.status, headers: responseHeaders })
}
